/**
 * The shape of a post, and the one place the contract's constants live.
 *
 * Posts are written by an automated writer and reviewed by a human. That only
 * works if the contract is narrow enough to state in a paragraph and strict
 * enough that a violation stops the build rather than reaching the site. So
 * every rule here is checked, unknown frontmatter keys are an error, and the
 * validator reports every problem in one pass instead of one per run.
 */

/** The five languages the site ships in. Same list as `scripts/locales.mjs`. */
export const POST_LOCALES = ['en', 'fr', 'de', 'it', 'es'] as const;

export type PostLocale = (typeof POST_LOCALES)[number];

/**
 * Google truncates a description around 155 characters, so a longer one is
 * not a longer description, it is a description with the end cut off.
 */
export const DESCRIPTION_LIMIT = 155;

/**
 * Google cuts a title around 60 characters. Past that it either truncates
 * mid-phrase or rewrites the title itself, and a rewritten title is one we
 * no longer control.
 *
 * This is the headline on its own. The " | Ledgerly" suffix is added only
 * when it still fits inside the same 60, so a title at the limit keeps its
 * own words instead of spending eleven of them on a brand the reader can
 * already see in the URL. See `blog-seo.service.ts`.
 */
export const TITLE_LIMIT = 60;

/** `<slug>.<locale>.md`, and the slug has to survive being a URL. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Where a post's image has to live.
 *
 * A local path, never a URL. An image referenced on somebody else's CDN makes
 * every reader's browser announce itself to a third party, on a site whose
 * whole claim is that it does not do that. The webhook that ingests articles
 * downloads their images into `apps/landing/public/assets/images/blog/` and
 * rewrites the reference, so this rule is what makes that non-optional.
 */
export const IMAGE_PATTERN =
  /^\/assets\/images\/blog\/[a-z0-9][a-z0-9/_-]*\.(?:png|jpg|jpeg|webp|avif|svg)$/;

/** Exactly the keys a post may declare. Anything else is a typo or an invention. */
export const FRONTMATTER_KEYS = [
  'title',
  'slug',
  'description',
  'date',
  'updated',
  'lang',
  'tags',
  'draft',
  'image',
] as const;

export interface PostFrontmatter {
  title: string;
  slug: string;
  description: string;
  /** `YYYY-MM-DD`. */
  date: string;
  /** `YYYY-MM-DD`, never earlier than `date`. */
  updated?: string;
  lang: PostLocale;
  tags: string[];
  draft: boolean;
  /** A local path under `/assets/images/blog/`. See IMAGE_PATTERN. */
  image?: string;
}

/** One parsed file: its frontmatter and its markdown body. */
export interface ParsedPost {
  /** Workspace-relative path, for error messages. */
  path: string;
  meta: PostFrontmatter;
  body: string;
}

/**
 * A published post as the site sees it: its frontmatter plus the locales the
 * same slug exists in. `locales` drives the hreflang set, so a post written in
 * English only never advertises a French alternate that would 404.
 */
export interface PostMeta extends PostFrontmatter {
  locales: PostLocale[];
}

export function isPostLocale(value: unknown): value is PostLocale {
  return (
    typeof value === 'string' &&
    (POST_LOCALES as readonly string[]).includes(value)
  );
}
